// Minimal, works in both browser and (optionally) server loaders/actions.
const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL ?? "https://api.nduka.io/api/v2";


type ApiOptions = RequestInit & {
  // If you call from a loader/action you can pass the incoming request to read cookies server-side
  requestCookieHeader?: string;
  json?: unknown;
};

export async function apiFetch(path: string, opts: ApiOptions = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const method = (opts.method ?? "GET").toUpperCase();
  const headers = new Headers(opts.headers || {});
  headers.set("Accept", "application/json");

  // JSON convenience
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  // For FormData, don't set Content-Type (let browser handle boundary)
  if (opts.body instanceof FormData) {
    headers.delete("Content-Type");
  }

  // Always include credentials so cookies flow
  const credentials: RequestCredentials = "include";

  // Better Auth handles security - no CSRF tokens needed

  const res = await fetch(url, {
    ...opts,
    method,
    headers,
    credentials,
    body: opts.json !== undefined ? JSON.stringify(opts.json) : opts.body,
  });

  return res;
}

/**
 * API Client for v2
 * Handles authentication, CSRF tokens, and error handling
 */

import { config } from './config';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: any
  ) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: any;
  params?: Record<string, string | number | boolean>;
  skipAuth?: boolean;
}

class ApiClient {
  private baseURL: string;

  constructor() {
    this.baseURL = config.api.baseUrl.replace(/\/$/, '');
  }

  /**
   * Main request method
   */
  public async request<T = any>(
    path: string,
    options: RequestOptions = {}
  ): Promise<T> {
    // For absolute URLs, use as-is. For relative paths, add /api/v2 prefix
    const fullPath = path.startsWith('http') 
      ? path 
      : `${this.baseURL}${path.startsWith('/') ? path : `/${path}`}`;
    
    const url = new URL(fullPath);

    // Add query params
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    // Prepare headers
    const headers = new Headers(options.headers);

    // Set content type for JSON
    if (options.body && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // Prepare request
    const requestOptions: RequestInit = {
      ...options,
      headers,
      credentials: 'include', // Include cookies
      body: options.body instanceof FormData 
        ? options.body 
        : options.body 
          ? JSON.stringify(options.body)
          : undefined,
    };

    try {
      const response = await fetch(url.toString(), requestOptions);

      // Handle response
      if (!response.ok) {
        let errorBody;
        try {
          errorBody = await response.json();
        } catch {
          errorBody = await response.text();
        }

        // Handle 401 - authentication required
        if (response.status === 401 && !options.skipAuth) {
          // Better Auth will handle session invalidation automatically
        }

        throw new ApiError(response.status, response.statusText, errorBody);
      }

      // Parse response
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      } else {
        return await response.text() as any;
      }
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Network error or other issues
      throw new ApiError(0, 'Network Error', error);
    }
  }

  // Convenience methods
  public get<T = any>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  public post<T = any>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  public put<T = any>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT', body });
  }

  public patch<T = any>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body });
  }

  public delete<T = any>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Note: Onboarding is now handled through Better Auth user profile updates
// and direct API calls to the core worker endpoints